import { signal, effect, computed } from '@preact/signals';
import { currentBible } from './bible-store.js';
import { route } from './store.js';
import { publisher } from '../publisher/mode.js';
import { secureStorage } from '../storage/secureStorage.js';

export type PlanId =
  | 'nt-90' | 'chrono-365' | 'psalms'
  | 'gospels-30' | 'proverbs-daily' | 'holy-week' | 'sermon-mount'
  | 'nt-psalms-180' | 'wisdom-60' | 'ot-365' | 'nt-40' | 'ot-40'
  | 'chrono-bible-40' | 'chrono-nt';

export type PlanTestament = 'nt' | 'ot' | 'both';

export type Plan = {
  id: PlanId;
  name: string;
  description: string;
  totalDays: number;
  loop?: boolean;
  days: { day: number; chapters: string[] }[];
};

export type PlanCatalogEntry = {
  id: PlanId;
  name: string;
  description: string;
  testament: PlanTestament;
};

export const PLAN_CATALOG: PlanCatalogEntry[] = [
  { id: 'nt-90',           name: 'New Testament in 90 days',          description: 'About 3 chapters a day.',                     testament: 'nt'   },
  { id: 'chrono-365',      name: 'Bible in a year (chronological)',    description: 'Full Bible in chronological order.',           testament: 'both' },
  { id: 'psalms',          name: 'Psalms a day',                       description: 'One Psalm per day, loops.',                    testament: 'ot'   },
  { id: 'gospels-30',      name: 'Gospels in 30 days',                 description: 'Matthew, Mark, Luke, and John in a month.',    testament: 'nt'   },
  { id: 'proverbs-daily',  name: 'Proverbs daily',                     description: 'One chapter per day, loops every 31 days.',    testament: 'ot'   },
  { id: 'holy-week',       name: 'Holy Week',                          description: 'Palm Sunday through Resurrection Sunday.',     testament: 'nt'   },
  { id: 'sermon-mount',    name: 'Sermon on the Mount',                description: 'Five days in Matthew 5-7 and Luke 6.',         testament: 'nt'   },
  { id: 'nt-psalms-180',   name: 'NT + Psalms in 6 months',           description: '1 NT chapter + 1 Psalm per day for 180 days.', testament: 'both' },
  { id: 'wisdom-60',       name: 'Wisdom literature in 60 days',       description: 'Job, Psalms, Proverbs, Ecclesiastes, Song.',   testament: 'ot'   },
  { id: 'ot-365',          name: 'Old Testament in a year',            description: 'All 929 OT chapters - about 2.5 per day.',     testament: 'ot'   },
  { id: 'nt-40',           name: 'New Testament in 40 days',           description: 'All 260 NT chapters - about 6 per day.',       testament: 'nt'   },
  { id: 'ot-40',           name: 'Old Testament in 40 days',           description: 'Curated survey of every major OT arc.',        testament: 'ot'   },
  { id: 'chrono-bible-40', name: 'Chronological Bible in 40 days',     description: 'Creation through new creation, surveyed.',     testament: 'both' },
  { id: 'chrono-nt',       name: 'Chronological New Testament',        description: 'NT books in the order they were written.',     testament: 'nt'   },
];

/**
 * Plans filtered by:
 *  1. publisher.readingPlans allow-list (when in publisher mode):
 *     - absent/undefined → no publisher restriction, apply scope filter only
 *     - []              → feature disabled, return empty
 *     - [id, ...]       → restrict to these IDs, then apply scope filter
 *  2. Bible scope — NT-only Bibles hide OT/'both' plans; OT-only Bibles hide NT/'both'.
 */
export const availablePlans = computed<PlanCatalogEntry[]>(() => {
  const bible = currentBible.value;
  const hasOT = bible.hasOT ?? true;
  const hasNT = bible.hasNT ?? true;

  const pub = publisher.value;
  const allowList = pub?.readingPlans;

  if (allowList !== undefined && allowList.length === 0) return [];

  return PLAN_CATALOG.filter((p) => {
    if (allowList !== undefined && !allowList.includes(p.id)) return false;
    if (p.testament === 'nt') return hasNT;
    if (p.testament === 'ot') return hasOT;
    return hasOT && hasNT;
  });
});

const ACTIVE_KEY = '_ap1';

type ActivePlan = { id: PlanId; startedAt: number; manualDay: number | null };

function load(): ActivePlan | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActivePlan>;
    if (typeof parsed.id !== 'string') return null;
    return {
      id: parsed.id as PlanId,
      startedAt: typeof parsed.startedAt === 'number' ? parsed.startedAt : Date.now(),
      manualDay: typeof parsed.manualDay === 'number' ? parsed.manualDay : null,
    };
  } catch {
    return null;
  }
}

export const activePlan = signal<ActivePlan | null>(load());
export const planData = signal<Record<string, Plan>>({});

if (typeof window !== 'undefined') {
  effect(() => {
    if (activePlan.value) {
      void secureStorage.setItem(ACTIVE_KEY, JSON.stringify(activePlan.value));
    } else {
      secureStorage.removeItem(ACTIVE_KEY);
    }
  });
}

export async function startPlan(id: PlanId): Promise<void> {
  const plan = await ensurePlanData(id);
  activePlan.value = { id, startedAt: Date.now(), manualDay: 1 };
  // Auto-advance the reader to the plan's starting passage so selecting a plan
  // (e.g. "Old Testament in 40 days" → Genesis 1) immediately moves the
  // scripture behind the settings to that plan's first reference. The user can
  // then start playback from the plan sheet OR straight from the player screen.
  const start = plan ? firstChapterOf(plan) : null;
  if (start) route.value = start;
}

/** First chapter reference of a plan's day 1, resolved to Bible Brain ids. */
function firstChapterOf(plan: Plan): { book: string; chapter: number } | null {
  const day1 = plan.days.find((d) => d.day === 1) ?? plan.days[0];
  const firstRef = day1?.chapters[0];
  if (!firstRef) return null;
  return parsePlanRef(firstRef);
}

/** Parse a plan day's OSIS-ish ref ("Gen.1") into Bible Brain { book, chapter }. */
function parsePlanRef(ref: string): { book: string; chapter: number } | null {
  const [bookOsis, chStr] = ref.split('.');
  if (!bookOsis || !chStr) return null;
  const book = OSIS_TO_BB[bookOsis];
  const chapter = Number(chStr);
  if (!book || !Number.isFinite(chapter)) return null;
  return { book, chapter };
}

export function stopPlan(): void {
  activePlan.value = null;
}

export async function ensurePlanData(id: PlanId): Promise<Plan | null> {
  if (planData.peek()[id]) return planData.peek()[id] ?? null;
  try {
    const res = await fetch(`/data/reading-plans/${id}.json`);
    if (!res.ok) return null;
    const plan = (await res.json()) as Plan;
    planData.value = { ...planData.peek(), [id]: plan };
    return plan;
  } catch {
    return null;
  }
}

export const currentPlanDay = computed<number | null>(() => {
  const active = activePlan.value;
  if (!active) return null;
  const plan = planData.value[active.id];
  if (!plan) return active.manualDay ?? 1;
  if (active.manualDay != null) return active.manualDay;
  const elapsedMs = Date.now() - active.startedAt;
  const elapsedDays = Math.floor(elapsedMs / 86_400_000);
  const idx = elapsedDays + 1;
  if (plan.loop) return ((idx - 1) % plan.totalDays) + 1;
  return Math.min(idx, plan.totalDays);
});

export function setPlanDay(day: number): void {
  const cur = activePlan.peek();
  if (!cur) return;
  activePlan.value = { ...cur, manualDay: day };
}

export const todayChapters = computed<{ book: string; chapter: number }[]>(() => {
  const active = activePlan.value;
  const day = currentPlanDay.value;
  if (!active || day == null) return [];
  const plan = planData.value[active.id];
  if (!plan) return [];
  const entry = plan.days.find((d) => d.day === day);
  if (!entry) return [];
  const out: { book: string; chapter: number }[] = [];
  for (const ref of entry.chapters) {
    const parsed = parsePlanRef(ref);
    if (parsed) out.push(parsed);
  }
  return out;
});

const OSIS_TO_BB: Record<string, string> = {
  Gen: 'GEN', Exod: 'EXO', Lev: 'LEV', Num: 'NUM', Deut: 'DEU',
  Josh: 'JOS', Judg: 'JDG', Ruth: 'RUT', '1Sam': '1SA', '2Sam': '2SA',
  '1Kgs': '1KI', '2Kgs': '2KI', '1Chr': '1CH', '2Chr': '2CH',
  Ezra: 'EZR', Neh: 'NEH', Esth: 'EST', Job: 'JOB', Ps: 'PSA',
  Prov: 'PRO', Eccl: 'ECC', Song: 'SNG', Isa: 'ISA', Jer: 'JER',
  Lam: 'LAM', Ezek: 'EZK', Dan: 'DAN', Hos: 'HOS', Joel: 'JOL',
  Amos: 'AMO', Obad: 'OBA', Jonah: 'JON', Mic: 'MIC', Nah: 'NAM',
  Hab: 'HAB', Zeph: 'ZEP', Hag: 'HAG', Zech: 'ZEC', Mal: 'MAL',
  Matt: 'MAT', Mark: 'MRK', Luke: 'LUK', John: 'JHN', Acts: 'ACT',
  Rom: 'ROM', '1Cor': '1CO', '2Cor': '2CO', Gal: 'GAL', Eph: 'EPH',
  Phil: 'PHP', Col: 'COL', '1Thess': '1TH', '2Thess': '2TH',
  '1Tim': '1TI', '2Tim': '2TI', Titus: 'TIT', Phlm: 'PHM', Heb: 'HEB',
  Jas: 'JAS', '1Pet': '1PE', '2Pet': '2PE', '1John': '1JN',
  '2John': '2JN', '3John': '3JN', Jude: 'JUD', Rev: 'REV',
};
